import { useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuraPoints } from './useAuraPoints';
import { toast } from 'sonner';

export interface OutfitRating {
  score: number;
  title: string;
  headline: string;
  strengths: string[];
  elevationTip: string;
  auraPoints: number;
  styleCategory: string;
  vibeCheck: string;
}

export interface RatingResult {
  rating: OutfitRating | null;
  insufficientCredits?: boolean;
  required?: number;
  available?: number;
}

export function useOutfitRating() {
  const [isRating, setIsRating] = useState(false);
  const [rating, setRating] = useState<OutfitRating | null>(null);
  const { addPoints } = useAuraPoints();

  const rateOutfit = useCallback(async (
    imageFile: File,
    context?: { weather?: any; timeOfDay?: string }
  ): Promise<RatingResult> => {
    setIsRating(true);
    setRating(null);

    try {
      // Convert file to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(imageFile);
      });

      console.log('[OOTD] Sending outfit for rating...');

      const { data, error } = await supabase.functions.invoke('rate-outfit', {
        body: {
          imageBase64: base64,
          context: {
            weather: context?.weather ? `${context.weather.temp}°C, ${context.weather.condition}` : undefined,
            timeOfDay: context?.timeOfDay || (new Date().getHours() < 18 ? 'daytime' : 'evening'),
          },
        },
      });

      if (error) throw error;

      // Handle insufficient credits
      if (data.error === 'insufficient_credits') {
        return {
          rating: null,
          insufficientCredits: true,
          required: data.required,
          available: data.available,
        };
      }

      if (data.error) {
        throw new Error(data.error);
      }

      console.log('[OOTD] Rating result:', data);
      setRating(data);

      // Award Aura Points based on score
      const pointsToAward = data.auraPoints || 15;
      
      // Custom points handling for OOTD
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('aura_points')
            .eq('id', user.id)
            .single();
          
          if (profile) {
            await supabase
              .from('profiles')
              .update({ aura_points: (profile.aura_points || 0) + pointsToAward })
              .eq('id', user.id);
          }
        }
      } catch (e) {
        console.error('Error awarding OOTD points:', e);
      }

      toast.success(`${data.vibeCheck}! +${pointsToAward} Aura`, {
        description: `Score: ${data.score}/100 - ${data.title}`,
      });

      return { rating: data };
    } catch (err) {
      console.error('[OOTD] Rating failed:', err);
      toast.error('Could not rate outfit. Please try again.');
      return { rating: null };
    } finally {
      setIsRating(false);
    }
  }, []);

  const reset = useCallback(() => {
    setRating(null);
    setIsRating(false);
  }, []);

  return {
    isRating,
    rating,
    rateOutfit,
    reset,
  };
}
